import { ExternalLinkIcon, RefreshIcon, TrashIcon } from "@heroicons/react/outline";
import { Check, Play, X } from "lucide-react";
import { ReactNode } from "react";

import { SignalTestResultState } from "../../types";
import { ASSERTION_REQUIRED_TOOLTIP, SIGNAL_COMPACT_REQUEST_ROW_HEIGHT_REM } from "../constants";
import { getSignalSampleRequestLabel } from "../utils";

import SignalCitationPanel from "./SignalCitationPanel";
import SignalReasonPanel from "./SignalReasonPanel";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { EntityIcon } from "@/components/ui/icons";
import { SignalSampleRequestLog, SignalTestResult } from "@/queries/signals";
import { EntityType } from "@/queries/folder-entities/types";

interface SampleRequestRowProps {
  requestLog: SignalSampleRequestLog;
  workspaceId: number;
  testResult: SignalTestResultState;
  testDetails?: SignalTestResult | null;
  errorMessage?: string;
  isTesting: boolean;
  canTest: boolean;
  onRemove: () => void;
  onTest: () => void;
}

const getResultBadge = (result: SignalTestResultState, errorMessage?: string): ReactNode => {
  if (result === SignalTestResultState.TRUE) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
        <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
        True
      </span>
    );
  }

  if (result === SignalTestResultState.FALSE) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
        <X className="h-3 w-3 shrink-0" aria-hidden="true" />
        False
      </span>
    );
  }

  if (result === SignalTestResultState.LOADING) {
    return (
      <Badge variant={BadgeVariant.BLUE} size={BadgeSize.SM} animate>
        <LoadingSpinner size={3} className="!mr-0.5" />
        Testing
      </Badge>
    );
  }

  if (result === SignalTestResultState.ERROR) {
    return <span className="text-xs font-medium text-red-600">{errorMessage ?? "Error"}</span>;
  }

  return null;
};

const SampleRequestRow = ({
  requestLog,
  workspaceId,
  testResult,
  testDetails,
  errorMessage,
  isTesting,
  canTest,
  onRemove,
  onTest,
}: SampleRequestRowProps) => {
  const preview =
    requestLog.prompt_preview ||
    requestLog.request_response_preview ||
    "No preview available for this request.";

  const isTrySignalDisabled = !canTest || isTesting;
  const hasCompletedTest =
    testResult === SignalTestResultState.TRUE || testResult === SignalTestResultState.FALSE;
  const showDetails =
    hasCompletedTest && Boolean(testDetails?.reasoning?.trim() || testDetails?.citation?.trim());

  return (
    <div
      className="px-5 py-4"
      style={showDetails ? undefined : { minHeight: `${SIGNAL_COMPACT_REQUEST_ROW_HEIGHT_REM}rem` }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <EntityIcon type={EntityType.PROMPT} size="xs" className="shrink-0" />
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-sm font-medium text-gray-900">
              {getSignalSampleRequestLabel(requestLog)}
            </p>
            <BasicTooltip content="Open Request Log">
              <Button
                type="button"
                size="tinyIcon"
                variant="ghost"
                className="h-5 w-5 shrink-0 p-0"
                onClick={() =>
                  window.open(
                    `/workspace/${workspaceId}/request/${requestLog.id}`,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                <ExternalLinkIcon className="h-3 w-3" />
              </Button>
            </BasicTooltip>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {testResult !== SignalTestResultState.IDLE && getResultBadge(testResult, errorMessage)}
          <BasicTooltip content={ASSERTION_REQUIRED_TOOLTIP} shouldShow={!canTest}>
            <span className="inline-flex">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-3 text-sm font-medium text-gray-900"
                onClick={onTest}
                disabled={isTrySignalDisabled}
              >
                {hasCompletedTest ? (
                  <RefreshIcon className="h-3 w-3 text-gray-500" />
                ) : (
                  <Play className="h-3 w-3 text-green-600" />
                )}
                {hasCompletedTest ? "Retry signal" : "Try signal"}
              </Button>
            </span>
          </BasicTooltip>
          <BasicTooltip content="Remove from sample">
            <Button
              type="button"
              size="tinyIcon"
              variant="ghost"
              className="h-8 w-8 shrink-0 text-gray-500 hover:text-gray-700"
              onClick={onRemove}
              disabled={isTesting}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </BasicTooltip>
        </div>
      </div>
      <p className={`mt-2 text-sm text-gray-500 ${showDetails ? "" : "line-clamp-2"}`}>{preview}</p>
      {showDetails ? (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {testDetails?.reasoning?.trim() ? (
            <SignalReasonPanel
              text={testDetails.reasoning.trim()}
              passed={testResult === SignalTestResultState.TRUE}
            />
          ) : null}
          {testDetails?.citation?.trim() ? (
            <SignalCitationPanel text={testDetails.citation.trim()} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SampleRequestRow;
